/**
 * @fileoverview Phone, cellphone, and extension fields for one additional contact.
 */

import React from "react";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import { IconButton } from "@mui/material";
import CountryCodeSelector from "../../CountryCodeSelector";
import { getSelectedCountryCodeOption } from "../../../utils/common";
import {
  CONTACT_INFORMATION_I18N,
  COUNTRY_CODE_SELECTOR_WIDTH,
} from "./additionalContactConstants";
import styles from "./ContactsData.module.css";

const AdditionalContactPhoneField = ({
  t,
  index,
  contact,
  countryCodeOptions,
  form,
  getFieldValidationState,
  getErrorMessage,
}) => {
  const {
    isTablet,
    selectedCountryCode,
    setSelectedCountryCode,
    showPhone,
    setShowPhone,
    handleInputChange,
    handleToggleChange,
  } = form;

  return (
    <div className={styles.inputGroup}>
      <label className={styles.inputLabel}>
        {t("components.contactsData.phoneLabel")}
      </label>
      <div
        className={`${styles.phoneInputContainer} ${
          getFieldValidationState(index, "telefono", contact?.telefono || "") ===
          "error"
            ? styles.phoneInputError
            : ""
        }`}
      >
        <CountryCodeSelector
          value={getSelectedCountryCodeOption(
            countryCodeOptions,
            selectedCountryCode,
          )}
          onChange={(value) => {
            setSelectedCountryCode(value.value);
            handleInputChange("telefonoCodigoPais", value.value);
          }}
          containerWidth={
            isTablet
              ? COUNTRY_CODE_SELECTOR_WIDTH.tablet
              : COUNTRY_CODE_SELECTOR_WIDTH.desktop
          }
          placeholder={t(CONTACT_INFORMATION_I18N.countryCodePlaceholder)}
        />
        <input
          type="tel"
          className={styles.input}
          placeholder={t("components.contactsData.phonePlaceholder")}
          value={contact?.telefono || ""}
          onChange={(e) => {
            const value = e.target.value;
            handleInputChange("telefono", value);
          }}
        />
      </div>
      {getErrorMessage(index, "telefono", contact?.telefono || "") && (
        <p className={styles.errorMessage}>
          {getErrorMessage(index, "telefono", contact?.telefono || "")}
        </p>
      )}
      <div className={styles.showInProfileContainer}>
        <span className={styles.showInProfileText}>
          {t(CONTACT_INFORMATION_I18N.showInProfile)}
        </span>
        <IconButton
          onClick={() => {
            setShowPhone(!showPhone);
            handleToggleChange("mostrarTelefono", !showPhone);
          }}
          size="small"
          className={styles.toggleButton}
          sx={{
            "& .MuiSvgIcon-root": {
              color: showPhone ? "#1d5ae8" : "#C7C8C8",
            },
          }}
        >
          {showPhone ? <ToggleOnIcon color="primary" /> : <ToggleOffIcon />}
        </IconButton>
      </div>
    </div>
  );
};

const AdditionalContactCellphoneField = ({
  t,
  index,
  contact,
  countryCodeOptions,
  form,
  getFieldValidationState,
  getErrorMessage,
}) => {
  const {
    isTablet,
    selectedCellphoneCountryCode,
    setSelectedCellphoneCountryCode,
    showCellphone,
    setShowCellphone,
    handleInputChange,
    handleToggleChange,
  } = form;

  return (
    <div className={styles.inputGroup}>
      <label className={styles.inputLabel}>
        {t("components.contactsData.cellphoneLabel")}
      </label>
      <div
        className={`${styles.phoneInputContainer} ${
          getFieldValidationState(index, "celular", contact?.celular || "") ===
          "error"
            ? styles.phoneInputError
            : ""
        }`}
      >
        <CountryCodeSelector
          value={getSelectedCountryCodeOption(
            countryCodeOptions,
            selectedCellphoneCountryCode,
          )}
          onChange={(value) => {
            setSelectedCellphoneCountryCode(value.value);
            handleInputChange("celularCodigoPais", value.value);
          }}
          containerWidth={
            isTablet
              ? COUNTRY_CODE_SELECTOR_WIDTH.tablet
              : COUNTRY_CODE_SELECTOR_WIDTH.desktop
          }
          placeholder={t(CONTACT_INFORMATION_I18N.countryCodePlaceholder)}
        />
        <input
          type="tel"
          className={styles.input}
          placeholder={t("components.contactsData.cellphonePlaceholder")}
          value={contact?.celular || ""}
          onChange={(e) => {
            const value = e.target.value;
            handleInputChange("celular", value);
          }}
        />
      </div>
      {getErrorMessage(index, "celular", contact?.celular || "") && (
        <p className={styles.errorMessage}>
          {getErrorMessage(index, "celular", contact?.celular || "")}
        </p>
      )}
      <div className={styles.showInProfileContainer}>
        <span className={styles.showInProfileText}>
          {t(CONTACT_INFORMATION_I18N.showInProfile)}
        </span>
        <IconButton
          onClick={() => {
            setShowCellphone(!showCellphone);
            handleToggleChange("mostrarCelular", !showCellphone);
          }}
          size="small"
          className={styles.toggleButton}
          sx={{
            "& .MuiSvgIcon-root": {
              color: showCellphone ? "#1d5ae8" : "#C7C8C8",
            },
          }}
        >
          {showCellphone ? (
            <ToggleOnIcon color="primary" />
          ) : (
            <ToggleOffIcon />
          )}
        </IconButton>
      </div>
    </div>
  );
};

const AdditionalContactExtensionField = ({
  t,
  index,
  contact,
  form,
  getFieldValidationState,
  getErrorMessage,
}) => {
  const {
    showExtension,
    setShowExtension,
    handleInputChange,
    handleToggleChange,
  } = form;

  return (
    <div className={styles.inputGroup}>
      <label className={styles.inputLabel}>
        {t("components.contactsData.extensionLabel")}
      </label>
      <input
        type="number"
        className={`${styles.input} ${
          getFieldValidationState(
            index,
            "extension",
            contact?.extension || "",
          ) === "error"
            ? styles.inputError
            : ""
        }`}
        placeholder={t("components.contactsData.extensionPlaceholder")}
        value={contact?.extension || ""}
        onChange={(e) => {
          const value = e.target.value;
          if (value.length <= 5) {
            handleInputChange("extension", value);
          }
        }}
      />
      {getErrorMessage(index, "extension", contact?.extension || "") && (
        <p className={styles.errorMessage}>
          {getErrorMessage(index, "extension", contact?.extension || "")}
        </p>
      )}
      <div className={styles.showInProfileContainer}>
        <span className={styles.showInProfileText}>
          {t(CONTACT_INFORMATION_I18N.showInProfile)}
        </span>
        <IconButton
          onClick={() => {
            setShowExtension(!showExtension);
            handleToggleChange("mostrarExtension", !showExtension);
          }}
          size="small"
          className={styles.toggleButton}
          sx={{
            "& .MuiSvgIcon-root": {
              color: showExtension ? "#1d5ae8" : "#C7C8C8",
            },
          }}
        >
          {showExtension ? (
            <ToggleOnIcon color="primary" />
          ) : (
            <ToggleOffIcon />
          )}
        </IconButton>
      </div>
    </div>
  );
};

export const AdditionalContactPhoneFields = (props) => (
  <>
    <AdditionalContactPhoneField {...props} />
    <AdditionalContactCellphoneField {...props} />
    <AdditionalContactExtensionField {...props} />
  </>
);
